import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import {
  BarcodeSearchResult,
  IProductListPatchModel,
  ProductListService,
} from '@epicuro-next/data/lists';
import { List } from '@epicuro-next/models/engagement';
import { SeverityLevel } from '@epicuro-next/models/platform/monitoring';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import {
  loadList,
  loadNextListProducts,
  QUERY_UPDATE_STRATEGY,
  resetListProducts,
} from '@epicuro-next/state/list-entities';
import { selectAccountId } from '@epicuro-next/state/session-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  bulkAddFetch,
  bulkAddFetchFailure,
  bulkAddFetchSuccess,
  bulkAddIdsChange,
  bulkAddItems,
  bulkAddItemsFailure,
  bulkAddItemsSuccess,
  loadProductList,
} from '../../actions';
import {
  selectListOldBulkAddStyleIdInput,
  selectOpenedList,
} from '../../selectors';
import { selectBulkAdd } from '../../selectors/bulk-add/product-list-bulk-add.selectors';

export const BULK_DEBOUNCE_TIME = 300;

@Injectable()
export class BulkAddEffects {
  public bulkAddIdsChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkAddIdsChange),
      map(({ ids }) => bulkAddFetch({ ids })),
    ),
  );

  public bulkAddFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkAddFetch),
      filter(({ ids }) => ids.length > 0),
      debounceTime(BULK_DEBOUNCE_TIME),
      concatLatestFrom(() =>
        this.store.pipe(select(selectAccountId), filter(isNonNullish)),
      ),
      switchMap(([{ ids }, accountId]) =>
        this.productListService.getProductsByBarcodes(accountId, ids).pipe(
          filter(Boolean),
          map((products: BarcodeSearchResult[]) =>
            bulkAddFetchSuccess({ products }),
          ),
          catchError(({ message, stack }) => {
            this.loggerService.exception({
              error: new Error(
                `[BulkAddEffects], bulkAddFetch.\n${message}\n${stack}`,
              ),
              severity: SeverityLevel.Error,
            });
            return of(bulkAddFetchFailure());
          }),
        ),
      ),
    ),
  );

  public bulkAddItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkAddItems),
      withLatestFrom(
        this.store.pipe(
          select(selectOpenedList),
          filter((v): v is List => !!v),
        ),
        this.store.pipe(select(selectBulkAdd)),
        this.store.pipe(select(selectListOldBulkAddStyleIdInput)),
      ),
      filter(([, , products]) => products.length > 0),
      switchMap(([, { id }, products, stylesOnly]) =>
        this.productListApi
          .changeProducts({
            id,
            patchItems: patchAddModel(products),
            stylesOnly,
          })
          .pipe(
            map(() => bulkAddItemsSuccess({ id })),
            catchError(({ message, stack }) => {
              this.loggerService.exception({
                error: new Error(`[BulkAddEffects].\n${message}\n${stack}`),
                severity: SeverityLevel.Error,
              });
              return of(bulkAddItemsFailure());
            }),
          ),
      ),
    ),
  );

  public bulkAddItemsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkAddItemsSuccess),
      map(({ id }) => loadProductList({ id })),
    ),
  );

  public pushNewListProductsToListEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkAddItemsSuccess),
      concatMap(({ id }) => [
        resetListProducts({ listId: id }),
        loadList({ listId: id }),
        loadNextListProducts({
          listId: id,
          strategy: QUERY_UPDATE_STRATEGY.OVERWRITE,
        }),
      ]),
    ),
  );

  constructor(
    private actions$: Actions,
    private productListService: ProductListService,
    private productListApi: ProductListApiService,
    private loggerService: LoggerService,
    private store: Store,
  ) {}
}

export const createSearchQueryString = (ids: string[]): string => {
  return ids.join(' ');
};

export const patchAddModel = (products: Product[]): IProductListPatchModel => {
  return {
    itemsToAdd: products.map((product) => ({
      productId: product.productId,
      brandId: product.brandId,
    })),
    itemsToDelete: [],
  };
};
